/**
 * Jertscript Lexer
 *
 * The lexer (scanner) that converts Jertscript source code into tokens.
 * "Honestly, TypeScript could use more croissants." 🥐
 */

import { Token, TokenType, createToken } from './tokens';
import { lookupKeyword } from './keywords';
import { LexerErrorType, getErrorMessage, formatErrorWithLine } from '../errors/messages';

/**
 * Lexer error with line information
 */
export interface LexerError {
  message: string;
  line: number;
}

/**
 * Lexer class that tokenizes Jertscript source code
 */
export class Lexer {
  private source: string;
  private tokens: Token[] = [];
  private errors: LexerError[] = [];

  // Position tracking
  private start = 0;   // Start of current lexeme
  private current = 0; // Current character position
  private line = 1;    // Current line number

  constructor(source: string) {
    this.source = source;
  }

  /**
   * Scans all tokens from the source code
   * @returns Array of tokens
   */
  scanTokens(): Token[] {
    while (!this.isAtEnd()) {
      // We are at the beginning of the next lexeme
      this.start = this.current;
      this.scanToken();
    }

    // Add EOF token at the end
    this.tokens.push(createToken(TokenType.EOF, '', null, this.line));
    return this.tokens;
  }

  /**
   * Returns any lexer errors encountered during scanning
   */
  getErrors(): LexerError[] {
    return this.errors;
  }

  /**
   * Checks if we've reached the end of the source
   */
  private isAtEnd(): boolean {
    return this.current >= this.source.length;
  }

  /**
   * Scans a single token
   */
  private scanToken(): void {
    const c = this.advance();

    switch (c) {
      // Single-character tokens
      case '(': this.addToken(TokenType.LPAREN); break;
      case ')': this.addToken(TokenType.RPAREN); break;
      case '{': this.addToken(TokenType.LBRACE); break;
      case '}': this.addToken(TokenType.RBRACE); break;
      case '[': this.addToken(TokenType.LBRACKET); break;
      case ']': this.addToken(TokenType.RBRACKET); break;
      case ',': this.addToken(TokenType.COMMA); break;
      case '.': this.addToken(TokenType.DOT); break;
      case '-': this.addToken(TokenType.MINUS); break;
      case '+': this.addToken(TokenType.PLUS); break;
      case ';': this.addToken(TokenType.SEMICOLON); break;
      case ':': this.addToken(TokenType.COLON); break;
      case '*': this.addToken(TokenType.STAR); break;

      // One or two character tokens
      case '!':
        this.addToken(this.match('=') ? TokenType.NEST_PAS : TokenType.PAS);
        break;
      case '=':
        if (this.match('=')) {
          this.addToken(this.match('=') ? TokenType.EST : TokenType.EQUAL_EQUAL);
        } else {
          this.addToken(TokenType.EQUAL);
        }
        break;
      case '<':
        this.addToken(this.match('=') ? TokenType.LESS_EQUAL : TokenType.LESS);
        break;
      case '>':
        this.addToken(this.match('=') ? TokenType.GREATER_EQUAL : TokenType.GREATER);
        break;

      // Two-character tokens
      case '&':
        if (this.match('&')) {
          this.addToken(TokenType.ET);
        } else {
          this.addError(LexerErrorType.INVALID_CHARACTER, { char: '&' });
        }
        break;
      case '|':
        if (this.match('|')) {
          this.addToken(TokenType.OU);
        } else {
          this.addError(LexerErrorType.INVALID_CHARACTER, { char: '|' });
        }
        break;

      // Slash - could be division or comment
      case '/':
        if (this.match('/')) {
          // Single-line comment - consume until end of line
          while (this.peek() !== '\n' && !this.isAtEnd()) {
            this.advance();
          }
        } else if (this.match('*')) {
          // Multi-line comment
          this.blockComment();
        } else {
          this.addToken(TokenType.SLASH);
        }
        break;

      // Whitespace
      case ' ':
      case '\r':
      case '\t':
        // Ignore whitespace
        break;
      case '\n':
        this.line++;
        break;

      // String literals
      case '"': this.string('"'); break;
      case "'": this.string("'"); break;

      default:
        if (this.isDigit(c)) {
          this.number();
        } else if (this.isAlpha(c)) {
          this.identifier();
        } else {
          this.addError(LexerErrorType.INVALID_CHARACTER, { char: c });
        }
        break;
    }
  }

  /**
   * Consumes the current character and returns it
   */
  private advance(): string {
    return this.source.charAt(this.current++);
  }

  /**
   * Looks at the current character without consuming it
   */
  private peek(): string {
    if (this.isAtEnd()) return '\0';
    return this.source.charAt(this.current);
  }

  /**
   * Looks at the next character without consuming it
   */
  private peekNext(): string {
    if (this.current + 1 >= this.source.length) return '\0';
    return this.source.charAt(this.current + 1);
  }

  /**
   * Conditionally consumes the current character if it matches expected
   */
  private match(expected: string): boolean {
    if (this.isAtEnd()) return false;
    if (this.source.charAt(this.current) !== expected) return false;
    this.current++;
    return true;
  }

  /**
   * Adds a token with no literal value
   */
  private addToken(type: TokenType): void {
    this.addTokenLiteral(type, null);
  }

  /**
   * Adds a token with a literal value
   */
  private addTokenLiteral(type: TokenType, literal: string | number | boolean | null): void {
    const text = this.source.substring(this.start, this.current);
    this.tokens.push(createToken(type, text, literal, this.line));
  }

  /**
   * Adds an error at the current line
   * @param errorType The type of lexer error
   * @param substitutions Optional substitutions for the error message
   */
  private addError(errorType: LexerErrorType, substitutions?: Record<string, string>): void {
    const message = getErrorMessage(errorType, substitutions);
    const formattedMessage = formatErrorWithLine(message, this.line);
    this.errors.push({ message: formattedMessage, line: this.line });
  }

  /**
   * Scans a string literal
   */
  private string(quote: string): void {
    while (this.peek() !== quote && !this.isAtEnd()) {
      if (this.peek() === '\n') this.line++;
      // Handle escape sequences
      if (this.peek() === '\\' && !this.isAtEnd()) {
        this.advance(); // consume the backslash
        if (!this.isAtEnd()) {
          this.advance(); // consume the escaped character
        }
      } else {
        this.advance();
      }
    }

    if (this.isAtEnd()) {
      this.addError(LexerErrorType.UNTERMINATED_STRING);
      return;
    }

    // The closing quote
    this.advance();

    // Trim the surrounding quotes and process escape sequences
    const value = this.source.substring(this.start + 1, this.current - 1);
    this.addTokenLiteral(TokenType.STRING, this.processEscapes(value));
  }

  /**
   * Process escape sequences in a string
   */
  private processEscapes(str: string): string {
    let result = '';
    let i = 0;
    while (i < str.length) {
      if (str[i] === '\\' && i + 1 < str.length) {
        const next = str[i + 1];
        switch (next) {
          case 'n': result += '\n'; break;
          case 't': result += '\t'; break;
          case 'r': result += '\r'; break;
          case '\\': result += '\\'; break;
          case '"': result += '"'; break;
          case "'": result += "'"; break;
          default: result += next; break;
        }
        i += 2;
      } else {
        result += str[i];
        i++;
      }
    }
    return result;
  }

  /**
   * Scans a number literal
   */
  private number(): void {
    while (this.isDigit(this.peek())) {
      this.advance();
    }

    // Look for a fractional part
    if (this.peek() === '.' && this.isDigit(this.peekNext())) {
      // Consume the "."
      this.advance();

      while (this.isDigit(this.peek())) {
        this.advance();
      }
    }

    const value = parseFloat(this.source.substring(this.start, this.current));
    this.addTokenLiteral(TokenType.NUMBER, value);
  }

  /**
   * Scans an identifier or keyword
   */
  private identifier(): void {
    while (this.isAlphaNumeric(this.peek())) {
      this.advance();
    }

    const text = this.source.substring(this.start, this.current);
    const type = lookupKeyword(text);

    // For boolean and null literals, set the appropriate literal value
    if (type === TokenType.OUI) {
      this.addTokenLiteral(type, true);
    } else if (type === TokenType.NON) {
      this.addTokenLiteral(type, false);
    } else if (type === TokenType.RIEN) {
      this.addTokenLiteral(type, null);
    } else {
      this.addToken(type);
    }
  }

  /**
   * Handles block comments /* ... *\/
   */
  private blockComment(): void {
    let depth = 1; // Support nested comments

    while (depth > 0 && !this.isAtEnd()) {
      if (this.peek() === '/' && this.peekNext() === '*') {
        this.advance();
        this.advance();
        depth++;
      } else if (this.peek() === '*' && this.peekNext() === '/') {
        this.advance();
        this.advance();
        depth--;
      } else {
        if (this.peek() === '\n') this.line++;
        this.advance();
      }
    }

    if (depth > 0) {
      this.addError(LexerErrorType.UNTERMINATED_COMMENT);
    }
  }

  /**
   * Checks if a character is a digit
   */
  private isDigit(c: string): boolean {
    return c >= '0' && c <= '9';
  }

  /**
   * Checks if a character is alphabetic (including underscore)
   */
  private isAlpha(c: string): boolean {
    return (c >= 'a' && c <= 'z') ||
           (c >= 'A' && c <= 'Z') ||
           c === '_';
  }

  /**
   * Checks if a character is alphanumeric
   */
  private isAlphaNumeric(c: string): boolean {
    return this.isAlpha(c) || this.isDigit(c);
  }
}

// Re-export Token types for convenience
export type { Token } from './tokens';
export { TokenType, createToken } from './tokens';
export { lookupKeyword, KEYWORDS } from './keywords';
export { LexerErrorType, getErrorMessage, formatErrorWithLine } from '../errors/messages';
